import { useCallback, useEffect, useMemo, useState } from 'react'

import { Select } from 'govwell-ui'
import { SelectOption } from 'govwell-ui/components/Select/types'
import { TimeZone } from 'types/graphql'

import { DateFormats, formatDateInTimeZone, TimeZoneDisplayMap } from 'src/utils/date'
import { TanstackField } from 'src/utils/forms'

const TIME_ZONES: TimeZone[] = ['Pacific', 'Mountain', 'MountainStandard', 'Central', 'Eastern']

const generateOption = (timeZone: TimeZone, currentTime: Date): SelectOption<TimeZone> => {
  let sublabel = `Current time: ${formatDateInTimeZone(currentTime, timeZone, DateFormats.Time)}`
  if (timeZone === 'MountainStandard') {
    sublabel += ` (used in states that do not observe daylight savings)`
  }
  return {
    value: timeZone,
    label: TimeZoneDisplayMap[timeZone],
    sublabel,
  }
}

const generateOptions = (currentTime: Date): SelectOption<TimeZone>[] =>
  TIME_ZONES.map((tz) => generateOption(tz, currentTime))

const getOptionKey = (option: SelectOption<TimeZone>) => option.value

type Props<TKey extends string> = {
  field: TanstackField<TimeZone | null | undefined, TKey>
  isRequired?: boolean
  label?: React.ReactNode
  placeholder?: string
}

const TimeZoneSelectField = <TKey extends string>({
  field,
  isRequired,
  label,
  placeholder,
}: Props<TKey>) => {
  // Re-render every minute so the "Current time" sublabel stays accurate without a manual refresh.
  const [currentTime, setCurrentTime] = useState(() => new Date())
  useEffect(() => {
    const msToNextMinute = 60_000 - (new Date().getSeconds() * 1000 + new Date().getMilliseconds())
    let interval: ReturnType<typeof setInterval> | undefined
    const timeout = setTimeout(() => {
      setCurrentTime(new Date())
      interval = setInterval(() => setCurrentTime(new Date()), 60_000)
    }, msToNextMinute)
    return () => {
      clearTimeout(timeout)
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [])

  const options = useMemo(() => generateOptions(currentTime), [currentTime])

  const selectedOption = useMemo(() => {
    return options.find((o) => o.value === field.state.value)
  }, [field.state.value, options])

  const handleSelectedOptionChange = useCallback(
    (option: SelectOption<TimeZone> | undefined) => {
      field.setValue(option?.value ?? null)
    },
    [field]
  )

  const errorMessage = field.state.meta.errors.join(', ')

  return (
    <Select<TimeZone>
      errorMessage={errorMessage}
      getOptionKey={getOptionKey}
      isInvalid={!!errorMessage}
      isRequired={isRequired}
      label={label}
      onBlur={field.handleBlur}
      onSelectedOptionChange={handleSelectedOptionChange}
      options={options}
      placeholder={placeholder}
      selectedOption={selectedOption}
    />
  )
}

export default TimeZoneSelectField
